import {
  CustomCheckbox,
  CustomCollapseCard,
  CustomForm,
  CustomFormItem,
  CustomImage,
  CustomInput,
  CustomMaskInput,
  CustomPopover,
  CustomSelect,
  CustomTextInput,
  Option,
  Text,
  useText,
} from '@src/components';
import { Alert, Button } from 'antd';
import { cardTypeIcon } from '@src/utils/utils';
import { useDispatch, useSelector } from 'react-redux';
import { monthsList, creditCardYears, VouIdCode } from '@src/store/enums';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formCreditCardRegex } from '@src/utils/formRule';
import { getBankOfBinNumber, setPayWithLinkinEmail } from '@src/store/slice/mobilExpressSlice';
import { useMediaQuery } from '@src/hooks/useMediaQuery';
import iconInfo from '@src/assets/icons/icon-bilgi.svg';
import dayjs from '@src/utils/day';
import iconBack from '@src/assets/icons/icon-back.svg';
import { loadBalancesUpdate } from '@src/store/slice/balanceUploadSlice';
import classNames from 'classnames';
import iconCircle from '@src/assets/icons/info-circle-white.svg';
import PropTypes from 'prop-types';
import warning from '@src/assets/icons/icon-profil-warning-dark.svg';

const NewCreditCards = ({ form, handleRegisteredCreditCard, counter, setCounter = () => {} }) => {
  const isMobil = useMediaQuery({ query: '(max-width: 991.98px)' });
  const { currentLoadBalance } = useSelector((state) => state?.balanceUpload);
  const { isSchool } = useSelector((state) => state?.auth);
  const [cardTypeImage, setCardTypeImage] = React.useState(false);
  const dispatch = useDispatch();
  const { storedCard, payWithLinkinEmail } = useSelector((state) => state?.mobilExpress);
  const [isStoreCard, setIsStoreCard] = useState(false);
  const [showPayLater, setShowPayLater] = useState(false);
  const [afterMount, setAfterMount] = useState(false);
  const isComplimentsVou = useMemo(
    () =>
      currentLoadBalance?.vouId === VouIdCode.Compliments ||
      currentLoadBalance?.vouId === VouIdCode.Market,
    [currentLoadBalance],
  );
  const activationDate = useMemo(() => {
    if (!currentLoadBalance?.activationDate) return '';

    const date = dayjs(currentLoadBalance.activationDate);
    return date.isValid() ? date.format('DD.MM.YYYY') : '';
  }, [currentLoadBalance?.activationDate]);
  const message = `Siparişinize ait ödeme linki, bakiye aktiflik tarihiniz olan ${activationDate}'da kayıtlı e-posta adresinize gönderilecektir. Ödemenizi size iletilen link üzerinden tamamlayabilirsiniz. Kart bakiyenizin aktif olması için ödemenizin de ${activationDate} tarihinde tamamlanması gerektiğini hatırlatırız.`;
  const nameOnCard = useText('nameOnCard');
  const monthText = useText('month');
  const yearText = useText('year');
  useEffect(() => {
    setCounter((count) => count + 1);
    return () => {
      setCounter((count) => count + 1);
    };
  }, []);

  const isValid = useMemo(() => {
    const values = form?.getFieldsValue();

    const cardNumber = values?.cardNumber?.replaceAll('_', '').replaceAll('-', '');
    const cardNumberLength = cardNumber?.length;
    const cardNumberIsValid = cardNumberLength === 16;

    const cardHolderName = values?.cardHolderName;
    const cardHolderNameIsValid = cardHolderName?.length > 0;

    const lastMonth = values?.lastMonth;
    const lastMonthIsValid = lastMonth > 0;

    const lastYear = values?.lastYear;
    const lastYearIsValid = lastYear?.length > 0;

    const cvv = values?.cvv?.replaceAll('_', '');
    const cvvIsValid = cvv?.length > 2;
    return (
      cardNumberIsValid &&
      cardHolderNameIsValid &&
      lastMonthIsValid &&
      lastYearIsValid &&
      cvvIsValid
    );
  });

  useEffect(() => {
    const today = dayjs().hour(23).minute(59).millisecond(0).second(59);
    const activationDate = dayjs(currentLoadBalance?.activationDate)
      .hour(23)
      .millisecond(0)
      .minute(59)
      .second(59);
    setShowPayLater(activationDate.isAfter(today));
    setAfterMount(activationDate.month() > today.month() || activationDate.year() > today.year());
  }, [currentLoadBalance]);

  useEffect(() => {
    dispatch(setPayWithLinkinEmail(false));
  }, []);

  useEffect(() => {
    if (payWithLinkinEmail && isStoreCard) {
      form.setFieldsValue({
        cardSaveChecked: false,
      });
    }
  }, [payWithLinkinEmail]);

  useEffect(() => {
    fetchOrder();
  }, []);

  const fetchOrder = useCallback(async () => {
    await dispatch(loadBalancesUpdate(currentLoadBalance));
  }, []);

  function handleSaveCardChecked(e) {
    setIsStoreCard(e.target.checked);
    dispatch(setPayWithLinkinEmail(false));
  }
  const handleOrderBack = useCallback(async () => {
    const body = {
      ...currentLoadBalance,
      orderStatus: 10,
    };

    await dispatch(loadBalancesUpdate(body));
  }, [currentLoadBalance, dispatch]);

  const cardNumberChange = useCallback(
    async (value) => {
      const cardNumber = value.replaceAll('_', '').replaceAll('-', '');
      if (cardNumber.length === 6) {
        const body = {
          bin: cardNumber,
        };
        const action = await dispatch(getBankOfBinNumber(body));
        if (getBankOfBinNumber.fulfilled.match(action)) {
          setCardTypeImage(cardTypeIcon(action.payload.data?.cardType));
        }
      }
    },
    [dispatch],
  );

  const isVerifiedPayment = useCallback(async () => {
    if (isSchool) {
      await dispatch(setPayWithLinkinEmail(true));
    } else if (afterMount && !isValid) {
      window.modalAction('payWithNewCard', true);
      return null;
    }
    window.handlePayment();
  }, [afterMount, isValid]);

  const handlePayLater = useCallback(async () => {
    if (!isValid) {
      window.modalAction('payWithNewCard', true);
      return null;
    }
    await window.payWhenBalanceActiveDay();
  }, [isValid]);

  const handlePayLaterCompliment = useCallback(async () => {
    try {
      if (!isValid) {
        window.modalAction('payWithNewCardForCompliment', true);
        return null;
      }
      await window.payWhenBalanceActiveDay();
    } catch (error) {
      console.error(error);
    }
  }, [isValid, form]);

  const getButtonText = () => {
    if (isComplimentsVou) return <Text t="confirmPayment" />;
    if (afterMount) return <Text t="continue" />;
    return <Text t="payNow" />;
  };

  const renderPayLaterButton = () =>
    isComplimentsVou ? (
      <button
        className={'compliments-payment-summary-later-pay'}
        style={{
          backgroundColor: '#162056',
          borderWidth: 1,
          marginBottom: 0,
          paddingTop: 14,
          paddingBottom: 14,
          paddingLeft: 4,
          paddingRight: 4,
          marginRight: 10,
        }}
        onClick={handlePayLater}
      >
        <span>
          <Text t="payLater" />
        </span>
        <CustomPopover
          id="ticketRestaurant-cardOrders-payment-newCreditCards-popover"
          placement="topRight"
          title={false}
          content={
            <div
              style={{
                width: '300px',
                color: '#3f4957',
                padding: '7px 18px',
              }}
            >
              <span>
                <Text t="onTheNextScreen" />{' '}
                <b className="compliments-bold-text">
                  <Text t="automaticCollectionOnTheDayTheBalanceBecomes" />
                </b>{' '}
                <Text t="or" />{' '}
                <b className="compliments-bold-text">
                  <Text t="paymentViaTheLinkSentToYourE" />
                </b>{' '}
                <Text t="youCanChooseOneOfTheMethods" />
              </span>
            </div>
          }
          trigger="hover"
          arrowPointAtCenter={true}
        >
          <div
            style={{
              marginRight: 0,
              marginBottom: 2,
            }}
          >
            <CustomImage src={iconCircle} />
          </div>
        </CustomPopover>
      </button>
    ) : (
      !isSchool && (
        <button
          className={classNames({
            'pay-when-balance-active-day-button': true,
          })}
          style={{ backgroundColor: 'transparent', borderWidth: 0 }}
          onClick={handlePayLater}
        >
          <span>
            <Text t="payLater" />
          </span>
          <CustomPopover
            id="ticketRestaurant-cardOrders-payment-newCreditCards-popover"
            placement="topRight"
            title={false}
            content={
              <div
                style={{
                  width: '300px',
                  color: '#3f4957',
                  padding: '7px 18px',
                }}
              >
                <Text t="ifYouSelectThisOptionTheTotalBalance" />
              </div>
            }
            trigger="hover"
            arrowPointAtCenter={true}
          >
            <div
              style={{
                marginRight: '24px',
                marginBottom: '7px',
              }}
            >
              <CustomImage src={iconInfo} />
            </div>
          </CustomPopover>
        </button>
      )
    );

  return (
    <>
      {afterMount && isSchool ? (
        <Alert
          message={
            <div className="alert-message-contain">
              <CustomImage src={warning} />{' '}
              <div className="description">
                <div>{message}</div>
              </div>
            </div>
          }
          banner
          showIcon={false}
          className="pay-later-alert-warning"
        />
      ) : (
        <div className="saved-credit-cards-card new-credit-card-page">
          <ParentElement
            storedCard={storedCard}
            handleRegisteredCreditCard={handleRegisteredCreditCard}
            isSchool={isSchool}
          >
            <div className="new-credit-card-content">
              <CustomForm
                name="creditCardForm"
                className="credit-card-form"
                initialValues={{
                  cardSaveChecked: false,
                }}
                autoComplete="off"
                layout={'vertical'}
                form={form}
              >
                <div className="row">
                  <div className="col-lg-6">
                    <CustomFormItem
                      label={<Text t="nameOnCard" />}
                      name="cardHolderName"
                      rules={[{ required: true, message: <Text t="pleaseEnterNameOnCard" /> }]}
                    >
                      <CustomTextInput
                        id="ticketRestaurant-balanceUpload-payment-newCreditCards-cardHolderName-textInput"
                        onInput={() => setCounter((count) => count + 1)}
                        placeholder={nameOnCard}
                      />
                    </CustomFormItem>
                  </div>
                  <div className="col-lg-6">
                    <CustomFormItem
                      label={<Text t="cardNumber2" />}
                      name="cardNumber"
                      className="card-number-form-item"
                      rules={[
                        { required: true, message: <Text t="pleaseEnterCardNumber" /> },
                        {
                          validator: formCreditCardRegex,
                          message: <Text t="pleaseEnterAValidCardNumber" />,
                        },
                        {
                          validator: async (field, value) => {
                            setCounter((count) => count + 1);
                            return Promise.resolve();
                          },
                          message: '',
                        },
                      ]}
                    >
                      <CardInput
                        id="ticketRestaurant-balanceUpload-payment-newCreditCards-cardNumber-cardInput"
                        onInput={() => setCounter((count) => count + 1)}
                        cardTypeImage={cardTypeImage}
                        cardNumberChange={cardNumberChange}
                      />
                    </CustomFormItem>
                  </div>
                </div>

                <div className="row">
                  <div className="col-lg-6">
                    <CustomFormItem
                      name={'expireMonthYear'}
                      className="expire-form-item"
                      label={
                        <span>
                          <Text t="expirationDate" />
                          <span className="form-required-mark">*</span>
                        </span>
                      }
                    >
                      <div className="row">
                        <div
                          className="ant-col-xs-12"
                          style={isMobil ? { padding: '0 10px 0 0' } : null}
                        >
                          <CustomFormItem
                            label={false}
                            name="lastMonth"
                            rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                          >
                            <CustomSelect
                              id="ticketRestaurant-balanceUpload-payment-newCreditCards-lastMonth-select"
                              onChange={() => setCounter((count) => count + 1)}
                              placeholder={monthText}
                            >
                              {monthsList.map((month) => (
                                <Option
                                  id={`ticketRestaurant-balanceUpload-payment-newCreditCards-lastMonth-${month?.value}-select`}
                                  key={month.value}
                                  value={month.value}
                                >
                                  {month.value}
                                </Option>
                              ))}
                            </CustomSelect>
                          </CustomFormItem>
                        </div>

                        <div
                          className="ant-col-xs-12"
                          style={isMobil ? { padding: '0 10px 0 0' } : null}
                        >
                          <CustomFormItem
                            label={false}
                            name="lastYear"
                            rules={[{ required: true, message: <Text t="requiredField2" /> }]}
                          >
                            <CustomSelect
                              id="ticketRestaurant-balanceUpload-payment-newCreditCards-lastYear-select"
                              onChange={() => setCounter((count) => count + 1)}
                              placeholder={yearText}
                            >
                              {creditCardYears.map((year) => (
                                <Option
                                  id={`ticketRestaurant-balanceUpload-payment-newCreditCards-lastYear-${year.value}-select`}
                                  key={year.value}
                                  value={year.value}
                                >
                                  {year.value}
                                </Option>
                              ))}
                            </CustomSelect>
                          </CustomFormItem>
                        </div>
                      </div>
                    </CustomFormItem>
                  </div>
                  <div className="col-lg-6">
                    <CustomFormItem
                      label={
                        <span>
                          <Text t="securityCode" />
                          <span className="form-required-mark">*</span>{' '}
                        </span>
                      }
                      name="cvv"
                      className="cvv-form-item"
                      rules={[
                        {
                          validator: async (field, val) => {
                            const value = val?.replace('_', '').split('');
                            try {
                              setCounter((count) => count + 1);
                              if (value.length === 3) {
                                return Promise.resolve();
                              }
                              return Promise.reject(new Error());
                            } catch (e) {
                              return Promise.reject(new Error());
                            }
                          },
                          message: <Text t="pleaseEnterTheSecurityCodeOnTheCard" />,
                        },
                      ]}
                    >
                      <CustomMaskInput
                        id="ticketRestaurant-balanceUpload-payment-newCreditCards-cvv-maskInput"
                        mask={'999'}
                      >
                        <CustomInput
                          id="ticketRestaurant-balanceUpload-payment-newCreditCards-cvv-input"
                          placeholder="CVC / CVV"
                        />
                      </CustomMaskInput>
                    </CustomFormItem>
                  </div>
                </div>

                <div className="info-text-contain">
                  <div className="col-md-12">
                    <div
                      style={{
                        fontFamily: 'UbuntuRegular, sans-serif',
                        fontSize: '14px',
                        fontWeight: 'normal',
                        fontStretch: 'normal',
                        fontStyle: 'normal',
                        lineHeight: 'normal',
                        letterSpacing: 'normal',
                        textAlign: 'left',
                        color: '#1d2228',
                      }}
                    >
                      <Text t="youCanSaveYourCardInformationToComplete2" />
                    </div>
                  </div>
                  <br />
                  {!isSchool && (
                    <div className="col-md-12">
                      <CustomFormItem label={false} name="cardSaveChecked" valuePropName="checked">
                        <CustomCheckbox
                          id="ticketRestaurant-balanceUpload-payment-newCreditCards-cardSaveChecked-checkbox"
                          value={isStoreCard}
                          onChange={handleSaveCardChecked}
                        >
                          <span className="info-text-title">
                            <Text t="iWantToSecurelySaveMyCardInformation" />
                          </span>
                        </CustomCheckbox>
                      </CustomFormItem>
                    </div>
                  )}
                </div>
              </CustomForm>
            </div>
          </ParentElement>
        </div>
      )}
      <div className={'pay-buttons-where-under-page'}>
        <div className="step-btn-container">
          <Button
            id="balanceUpload-payment-newCreditCards-back-btn"
            className="back-btn"
            onClick={handleOrderBack}
          >
            <CustomImage src={iconBack} />
            <Text t="turnBack" />
          </Button>
        </div>
        {showPayLater && !afterMount && <>{renderPayLaterButton()}</>}

        <Button
          id="balanceUpload-payment-newCreditCards-payment-btn"
          disabled={afterMount ? false : !isValid}
          onClick={isVerifiedPayment}
          className="payment-btn"
        >
          {getButtonText()}
        </Button>
      </div>
    </>
  );
};

NewCreditCards.propTypes = {
  form: PropTypes.any,
  handleRegisteredCreditCard: PropTypes.func,
  counter: PropTypes.number,
  setCounter: PropTypes.func,
};

export default NewCreditCards;

const CardInput = ({ cardTypeImage, cardNumberChange, onChange, ...props }) => {
  const enterYour16DigitCardNumber = useText('enterYour16DigitCardNumber');
  const change = (e) => {
    cardNumberChange?.(e.target.value);
    onChange?.(e);
  };

  return (
    <CustomMaskInput
      id="companyInformation-creditCards-newCreditCard-creditCardForm-card-maskInput"
      {...props}
      mask={'9999-9999-9999-9999'}
      onChange={change}
    >
      <CustomInput
        id="companyInformation-creditCards-newCreditCard-creditCardForm-card-input"
        placeholder={enterYour16DigitCardNumber}
        suffix={cardTypeImage ? <CustomImage src={cardTypeImage} /> : <span />}
      />
    </CustomMaskInput>
  );
};

CardInput.propTypes = {
  cardTypeImage: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  cardNumberChange: PropTypes.func,
  onChange: PropTypes.func,
};

const ParentElement = ({ children, storedCard, handleRegisteredCreditCard, isSchool }) => {
  return (
    <CustomCollapseCard
      headerRightInfo={
        storedCard.length > 0 &&
        !isSchool && (
          <div className="header-btn" onClick={handleRegisteredCreditCard}>
            <Text t="iWantToPayWithMyRegisteredCredit" />
          </div>
        )
      }
      cardTitle={
        <div className="title">
          <Text t="savedCreditCardsTitle" />
        </div>
      }
    >
      <div className="custom-card-body">{children}</div>
    </CustomCollapseCard>
  );
};

ParentElement.propTypes = {
  children: PropTypes.node,
  storedCard: PropTypes.array,
  handleRegisteredCreditCard: PropTypes.func,
  isSchool: PropTypes.bool,
};
